import { sanitizeUrl } from './urlSanitizer'
import { collectDevice } from './device'
import { installConsolePatch } from './console'
import { installFetchPatch, installXhrPatch } from './network'
import { installErrorHandlers } from './errors'
import { createPerformanceCollector } from './performance'
import { RingBuffer } from './ringBuffer'
import type { CapturedContext, ConsoleEntry, ErrorEntry, NetworkEntry } from '../types'

export interface CaptureOptions {
  sanitizeUrl?: (url: string) => string
  maxConsole?: number
  maxNetwork?: number
  maxErrors?: number
}

export interface CaptureHandle {
  snapshot(): CapturedContext
  clear(): void
  dispose(): void
}

export function installCapture(options: CaptureOptions = {}): CaptureHandle {
  const { maxConsole = 50, maxNetwork = 50, maxErrors = 20 } = options
  const sanitize = options.sanitizeUrl ?? sanitizeUrl

  const consoleBuf = new RingBuffer<ConsoleEntry>(maxConsole)
  const networkBuf = new RingBuffer<NetworkEntry>(maxNetwork)
  const errorBuf = new RingBuffer<ErrorEntry>(maxErrors)

  const uninstallConsole = installConsolePatch(consoleBuf)
  const uninstallFetch = installFetchPatch(networkBuf, sanitize)
  const uninstallXhr = installXhrPatch(networkBuf, sanitize)
  const uninstallErrors = installErrorHandlers(errorBuf)
  const perf = createPerformanceCollector()

  return {
    snapshot(): CapturedContext {
      return {
        consoleLogs: consoleBuf.snapshot(),
        networkRequests: networkBuf.snapshot(),
        errors: errorBuf.snapshot(),
        device: collectDevice(),
        capturedAt: Date.now(),
      }
    },
    clear() {
      consoleBuf.clear(); networkBuf.clear(); errorBuf.clear()
    },
    dispose() {
      uninstallConsole(); uninstallFetch(); uninstallXhr(); uninstallErrors()
      perf.dispose()
    },
  }
}
